/* ============================================================================
   INBAR+co Cart Drawer UI
   ============================================================================
   Same architecture as inbar-cart.js. Binds drawer UI controls to
   ICCart.mutate. Re-renders the drawer section via Section Rendering API
   when 'ic:cart:changed' fires.

   We do NOT own the drawer's open/close animation -- that's Prestige's
   <cart-drawer> Web Component. We only manage the content inside it.

   This file does NOT call /cart/* endpoints directly. Ever.
   ============================================================================ */

(function () {
  'use strict';

  const SECTION_ID = 'cart-drawer';
  let busy = false;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    bindHandlers();
    document.addEventListener('ic:cart:changed', onCartChanged);
    document.addEventListener('ic:cart:error', onCartError);
    // When Prestige opens the drawer, refresh state from server in case
    // something changed on another tab or via a third-party script
    document.addEventListener('cart:open', function () {
      if (window.ICCart) window.ICCart.refresh();
    });
  }

  function bindHandlers() {
    const root = document.querySelector('[data-ic-cart-drawer]');
    if (!root) return;
    bindQtyButtons(root);
    bindQtyInputs(root);
    bindRemoveButtons(root);
    bindCrossSellForms(root);
    bindCloseHandlers(root);
  }

  // --------------------------------------------------------------------------
  // Quantity stepper (+ / -)
  // --------------------------------------------------------------------------
  function bindQtyButtons(root) {
    root.querySelectorAll('[data-ic-qty]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (busy) return;
        const action = btn.getAttribute('data-ic-qty');
        const lineKey = btn.getAttribute('data-line-key');
        const input = root.querySelector(
          '[data-ic-qty-input][data-line-key="' + lineKey + '"]'
        );
        if (!input) return;
        const current = parseInt(input.value, 10) || 1;
        const next = action === 'plus' ? current + 1 : Math.max(0, current - 1);
        if (next === current) return;
        input.value = next;
        setBusy(true);
        if (next === 0) {
          window.ICCart.mutate({ type: 'remove', key: lineKey });
        } else {
          window.ICCart.mutate({ type: 'change', key: lineKey, quantity: next });
        }
      });
    });
  }

  function bindQtyInputs(root) {
    root.querySelectorAll('[data-ic-qty-input]').forEach(function (input) {
      let initialValue = input.value;
      input.addEventListener('focus', function () { initialValue = input.value; });
      input.addEventListener('change', function () {
        if (busy) return;
        const lineKey = input.getAttribute('data-line-key');
        const value = parseInt(input.value, 10);
        if (isNaN(value) || value < 0) {
          input.value = initialValue;
          return;
        }
        if (value === parseInt(initialValue, 10)) return;
        setBusy(true);
        if (value === 0) {
          window.ICCart.mutate({ type: 'remove', key: lineKey });
        } else {
          window.ICCart.mutate({ type: 'change', key: lineKey, quantity: value });
        }
      });
    });
  }

  // --------------------------------------------------------------------------
  // Remove button (X)
  // --------------------------------------------------------------------------
  function bindRemoveButtons(root) {
    root.querySelectorAll('[data-ic-remove]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (busy) return;
        const lineKey = btn.getAttribute('data-ic-remove');
        const lineEl = btn.closest('.ic-cd-line');
        const isGwpDismiss = !!(lineEl && lineEl.classList.contains('ic-cd-line--gwp'));
        setBusy(true);
        window.ICCart.mutate({
          type: 'remove',
          key: lineKey,
          isGwpDismiss: isGwpDismiss
        });
      });
    });
  }

  // --------------------------------------------------------------------------
  // Cross-sell add forms
  // --------------------------------------------------------------------------
  function bindCrossSellForms(root) {
    root.querySelectorAll('[data-ic-cross-sell-form]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (busy) return;
        const variantInput = form.querySelector('input[name="id"]');
        if (!variantInput) return;
        const btn = form.querySelector('button[type="submit"]');
        if (btn) {
          btn.disabled = true;
          btn.dataset.originalText = btn.textContent;
          btn.textContent = 'Adding...';
        }
        setBusy(true);
        window.ICCart.mutate({ type: 'add', id: variantInput.value, quantity: 1 })
          .catch(function () {
            if (btn) {
              btn.disabled = false;
              btn.textContent = btn.dataset.originalText || 'Add to Bag';
            }
          });
      });
    });
  }

  // --------------------------------------------------------------------------
  // Close handlers
  // --------------------------------------------------------------------------
  function bindCloseHandlers(root) {
    const closeBtn = root.querySelector('.ic-cd__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        closeDrawer();
      });
    }
    const continueBtn = root.querySelector('[data-ic-continue]');
    if (continueBtn) {
      continueBtn.addEventListener('click', function (e) {
        e.preventDefault();
        closeDrawer();
      });
    }
  }

  function closeDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    if (typeof drawer.hide === 'function') drawer.hide();
    else if (typeof drawer.close === 'function') drawer.close();
    else drawer.removeAttribute('open');
  }

  // --------------------------------------------------------------------------
  // React to cart changes
  // --------------------------------------------------------------------------
  function onCartChanged() {
    rerenderSection();
  }

  function onCartError(e) {
    console.error('IC Drawer: error event', e.detail && e.detail.error);
    rerenderSection();
  }

  function rerenderSection() {
    fetch('/?section_id=' + SECTION_ID, {
      method: 'GET',
      headers: { 'Accept': 'text/html' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Section fetch failed');
        return r.text();
      })
      .then(function (html) {
        swapSection(html);
        setBusy(false);
      })
      .catch(function (err) {
        console.error('IC Drawer: section render failed', err);
        window.location.reload();
      });
  }

  function swapSection(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-ic-cart-drawer]');
    const current = document.querySelector('[data-ic-cart-drawer]');
    if (!fresh || !current) {
      window.location.reload();
      return;
    }
    current.parentNode.replaceChild(fresh, current);
    bindHandlers();
  }

  function setBusy(isBusy) {
    busy = isBusy;
    const root = document.querySelector('[data-ic-cart-drawer]');
    if (!root) return;
    if (isBusy) {
      root.style.opacity = '0.7';
      root.style.transition = 'opacity 0.08s ease';
      root.style.pointerEvents = 'none';
    } else {
      root.style.opacity = '';
      root.style.pointerEvents = '';
    }
  }
})();
